import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useCurrentLanguage, useTranslator } from 'tradera/lang/translate';
import Tags from '../tags/tags';
import { fetchPersonalisedSearchesIfNeeded } from 'tradera/state/personalisation/actions';
import css from './personalised-tags.module.scss';
import { translationDynamicApiClient } from 'tradera/utils/http';
import { selectAutomaticTranslationPreferred } from 'tradera/state/language/selectors';
import qs from 'qs';
import useIsMobile from 'tradera/hooks/use-is-mobile';
import { StartPageSectionTitle } from '../start-page-section/start-page-section-title';
import { useGetTrendingSearchesQuery } from 'tradera/state/services/discover-api';

const mapTrendingSearchesToTags = (trendingSearches) =>
    trendingSearches?.map((search) => ({
        index: search.index,
        name: search.query || '',
        url: `/search?q=${search.query}${
            search.categoryId ? `&categoryId=${search.categoryId}` : ''
        }&sortBy=MostBids`
    }));

export const PersonalisedSearchesTags = () => {
    const scroll = useIsMobile();
    const { t } = useTranslator();
    const dispatch = useDispatch();
    const showTrendingSearches = useShowTrendingSearches();
    const { language } = useCurrentLanguage();
    const automaticTranslationPreferred = useSelector(
        selectAutomaticTranslationPreferred
    );

    const { data: trendingSearchesItems } = useGetTrendingSearchesQuery(
        undefined,
        {
            skip: !showTrendingSearches
        }
    );

    const trendingSearchesTags = React.useMemo(
        () => mapTrendingSearchesToTags(trendingSearchesItems),
        [trendingSearchesItems]
    );

    const personalisedSearchesItems = useSelector(
        (state) => state.personalisation.searches.items
    );

    const stateTags = showTrendingSearches
        ? trendingSearchesTags
        : personalisedSearchesItems;
    const [tags, setTags] = useState([]);

    useEffect(() => {
        if (!showTrendingSearches) {
            dispatch(fetchPersonalisedSearchesIfNeeded());
        }
    }, [dispatch, showTrendingSearches]);

    const shouldTranslateOnLoad =
        automaticTranslationPreferred && language?.toLowerCase() === 'da';

    useEffect(() => {
        const translateTags = async () => {
            if (!stateTags || stateTags.length === 0) {
                return;
            }

            const { htmlTexts } = await translationDynamicApiClient.command(
                'gettranslatedtexts',
                {
                    htmlTexts: stateTags.map((x) => x.name),
                    toLanguageCodeIso2: language,
                    fromLanguageCodeIso2: 'sv',
                    type: 6
                }
            );
            if (htmlTexts) {
                setTags(
                    htmlTexts.map((text, index) => {
                        const tag = stateTags.find(
                            (x) => x.index === index + 1
                        );

                        const [pathname, search] = tag.url.split('?');
                        const searchParams = qs.parse(search, {
                            ignoreQueryPrefix: true
                        });
                        const url = `${pathname}?${qs.stringify({
                            ...searchParams,
                            q: text
                        })}`;
                        return {
                            ...tag,
                            name: text,
                            url
                        };
                    })
                );
            }
        };
        if (shouldTranslateOnLoad) {
            translateTags();
        } else {
            setTags(stateTags);
        }
    }, [shouldTranslateOnLoad, language, stateTags]);

    const isLoading = useIsPersonalisedSearchesTagsLoading();

    if (isLoading) {
        // Approximated height of component when loaded
        return <div className={css.loading} />;
    }

    if (!tags?.length) return null;

    return (
        <div>
            <StartPageSectionTitle noMargins className="mx-2 mx-md-0">
                {showTrendingSearches
                    ? t('trendingTags_searches')
                    : t('personalised_suggestions_searches')}
            </StartPageSectionTitle>
            {/* Forcing flex here makes Tags scrollable in this grid container */}
            <div className="d-flex">
                <Tags
                    gaCategory={
                        showTrendingSearches
                            ? 'Personalised Searches fallback'
                            : 'Personalised Searches'
                    }
                    gaAction="Navigation"
                    tags={tags}
                    scroll={scroll}
                    padOuterItems={scroll}
                />
            </div>
        </div>
    );
};

export const useShowTrendingSearches = () =>
    useSelector(
        (state) => state.personalisation.searches.status === 'HAS_NO_DATA'
    );

const useIsPersonalisedSearchesLoading = () => {
    const personalisedSearchesStatus = useSelector(
        (state) => state.personalisation.searches.status
    );
    return ['NOT_FETCHED', 'LOADING'].includes(personalisedSearchesStatus);
};

const useIsTrendingSearchesLoading = () => {
    const showTrendingSearches = useShowTrendingSearches();
    const { isFetching } = useGetTrendingSearchesQuery(undefined, {
        skip: !showTrendingSearches
    });
    return isFetching;
};

export const useIsPersonalisedSearchesTagsLoading = () => {
    const isPersonalisedSearchesLoading = useIsPersonalisedSearchesLoading();
    const isTrendingSearchesLoading = useIsTrendingSearchesLoading();
    return isPersonalisedSearchesLoading || isTrendingSearchesLoading;
};
